/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useCallback, useEffect, useState } from "react";
import { watchMediaStalls } from "../livekit/mediaStallWatchdog";
import {
  ConnectionState,
  type DisconnectReason,
  type LocalTrackPublication,
  type Room as LivekitRoom,
  RoomEvent,
  Track,
} from "livekit-client";
import {
  MatrixRTCSessionEvent,
  isLivekitFocus,
  type MatrixRTCSession,
} from "matrix-js-sdk/lib/matrixrtc";

import type { Subscription } from "rxjs";
import type { MatrixClient, Room as MatrixRoom } from "matrix-js-sdk";
import { useInitial } from "../useInitial";
import { useLatest } from "../useLatest";
import type { MuteStates } from "../room/MuteStates";
import type { MediaDevices } from "../state/MediaDevices";
import {
  createRoomMembersHydrator,
  type RoomMembersHydrationAdapter,
} from "../matrix/RoomMembersHydrator";
import {
  createRtcMembersHydrator,
  type RtcMembersHydrationAdapter,
} from "../matrix/RtcMembersHydrator";
import { DefaultLiveKitRecoveryAdapter } from "../livekit/LiveKitRecoveryAdapter";
import { createSFUCredentialsAdapter } from "../livekit/openIDSFU";
import { classifyCallRecoveryFailure } from "./CallRecoveryErrorClassifier";
import {
  DefaultCallRecoveryCoordinator,
  type CallRecoveryCoordinator,
} from "./CallRecoveryCoordinator";
import type {
  CallRecoveryPhase,
  CallRecoverySignal,
  TerminalCallError,
} from "./CallRecoveryTypes";
import {
  browserCallRecoveryNetwork,
  DefaultFocusRecoveryAdapter,
  DefaultLocalMediaRecoveryAdapter,
  DefaultMatrixRecoveryAdapter,
  DefaultOwnMembershipRecoveryAdapter,
} from "./ActiveCallRecoveryAdapters";
import { DefaultCallPresentationStore } from "./CallPresentationStore";
import type { CallPresentationState } from "./CallRecoveryAdapters";
import type { Behavior } from "../state/Behavior";
import type { BuildTechnicalRTCSessionJoinOptions } from "../rtcSessionHelpers";
import type { CallAutoRageshakeApi } from "../room/useCallAutoRageshake";
import { CallRecoveryTelemetryObserver } from "./CallRecoveryTelemetry";

/**
 * Stable delegate принимает ранние сигналы hook-ов до установки coordinator.
 * Очередь нужна только в пределах одного синхронного mount и не создаёт retry.
 */
export class CallRecoverySignalRelay {
  private handler: ((signal: CallRecoverySignal) => boolean) | null = null;
  private readonly pending: CallRecoverySignal[] = [];
  private recoveryPhase: CallRecoveryPhase = "idle";
  private readonly recoveryPhaseListeners = new Set<
    (phase: CallRecoveryPhase) => void
  >();

  public emit = (signal: CallRecoverySignal): boolean => {
    if (this.handler) return this.handler(signal);
    this.pending.push(signal);
    // Сигнал уже принят relay и будет доставлен после mount coordinator.
    // Возвращаем true, чтобы ранняя ошибка standard flow не ушла параллельно
    // в fatal boundary.
    return true;
  };

  public attach(handler: (signal: CallRecoverySignal) => boolean): () => void {
    this.handler = handler;
    for (const signal of this.pending.splice(0)) handler(signal);
    return () => {
      if (this.handler === handler) this.handler = null;
    };
  }

  public getRecoveryPhase = (): CallRecoveryPhase => this.recoveryPhase;

  public subscribeRecoveryPhase = (
    listener: (phase: CallRecoveryPhase) => void,
  ): (() => void) => {
    this.recoveryPhaseListeners.add(listener);
    listener(this.recoveryPhase);
    return (): void => {
      this.recoveryPhaseListeners.delete(listener);
    };
  };

  public setRecoveryPhase(phase: CallRecoveryPhase): void {
    if (this.recoveryPhase === phase) return;
    this.recoveryPhase = phase;
    for (const listener of this.recoveryPhaseListeners) listener(phase);
  }
}

export interface ActiveCallRecoveryResult {
  readonly coordinator: CallRecoveryCoordinator;
  readonly roomMembersHydrator: RoomMembersHydrationAdapter;
  readonly rtcMembersHydrator: RtcMembersHydrationAdapter;
  readonly presentation: DefaultCallPresentationStore;
  readonly presentationState$: Behavior<CallPresentationState>;
  readonly recoveryActive: boolean;
  readonly cancel: (reason: "user_leave" | "unmount" | "host_hangup") => void;
}

interface ActiveCallRecoveryOptions {
  readonly callSessionId: string;
  readonly client: MatrixClient;
  readonly matrixRoom: MatrixRoom;
  readonly rtcSession: MatrixRTCSession;
  readonly livekitRoom: LivekitRoom;
  readonly muteStates: MuteStates;
  readonly mediaDevices: MediaDevices;
  readonly encryptMedia: boolean;
  readonly useNewMembershipManager: boolean;
  readonly useExperimentalToDeviceTransport: boolean;
  readonly notificationType?: BuildTechnicalRTCSessionJoinOptions["notificationType"];
  readonly callIntent?: BuildTechnicalRTCSessionJoinOptions["callIntent"];
  readonly relay: CallRecoverySignalRelay;
  readonly onTerminal: (error: TerminalCallError) => void;
  readonly autoRageshake?: CallAutoRageshakeApi;
}

/**
 * Единственный React composition root hard recovery активной call session.
 * Внутри него сходятся Matrix, LiveKit и media signals; pipeline остаётся
 * framework-agnostic.
 */
export function useActiveCallRecovery({
  callSessionId,
  client,
  matrixRoom,
  rtcSession,
  livekitRoom,
  muteStates,
  mediaDevices,
  encryptMedia,
  useNewMembershipManager,
  useExperimentalToDeviceTransport,
  notificationType,
  callIntent,
  relay,
  onTerminal,
  autoRageshake,
}: ActiveCallRecoveryOptions): ActiveCallRecoveryResult {
  const latestMuteStates = useLatest(muteStates);
  const latestAutoRageshake = useLatest(autoRageshake);
  const ownership = useInitial(() => {
    const roomMembersHydrator = createRoomMembersHydrator(client, matrixRoom);
    const rtcMembersHydrator = createRtcMembersHydrator(client, matrixRoom);
    const focus = new DefaultFocusRecoveryAdapter(rtcSession);
    const presentation = new DefaultCallPresentationStore(onTerminal);
    const coordinator = new DefaultCallRecoveryCoordinator(
      callSessionId,
      {
        matrix: new DefaultMatrixRecoveryAdapter(
          client,
          matrixRoom,
        ),
        membership: new DefaultOwnMembershipRecoveryAdapter(rtcSession, {
          encryptMedia,
          useNewMembershipManager,
          useExperimentalToDeviceTransport,
          notificationType,
          callIntent,
        }),
        focus,
        credentials: createSFUCredentialsAdapter(
          client,
          () => {
            const current = rtcSession.getActiveFocus();
            return current && isLivekitFocus(current) ? current : undefined;
          },
          client.getDomain() ?? "",
        ),
        livekit: new DefaultLiveKitRecoveryAdapter(livekitRoom),
        media: new DefaultLocalMediaRecoveryAdapter(
          livekitRoom,
          () => latestMuteStates.current!,
          mediaDevices,
        ),
        presentation,
        classify: classifyCallRecoveryFailure,
      },
      { network: browserCallRecoveryNetwork },
    );
    const telemetry = new CallRecoveryTelemetryObserver({
      reportIncident: (incident): void => {
        latestAutoRageshake.current?.reportIncident(incident);
      },
    });
    return {
      coordinator,
      telemetry,
      roomMembersHydrator,
      rtcMembersHydrator,
      presentation,
    };
  });
  const deferredUnmount = useInitial(
    (): { timer: ReturnType<typeof setTimeout> | null } => ({ timer: null }),
  );
  const telemetryLifecycle = useInitial(
    (): { subscription: Subscription | null } => ({ subscription: null }),
  );
  const [recoveryActive, setRecoveryActive] = useState(false);

  useEffect(() => {
    if (telemetryLifecycle.subscription === null) {
      telemetryLifecycle.subscription = ownership.coordinator.state$.subscribe(
        (state) => ownership.telemetry.observe(state),
      );
    }
    const subscription = ownership.coordinator.state$.subscribe((state) => {
      relay.setRecoveryPhase(state.phase);
      setRecoveryActive(ownership.presentation.state$.value.recoveryActive);
    });
    const presentationSubscription = ownership.presentation.state$.subscribe(
      (state) => setRecoveryActive(state.recoveryActive),
    );
    return (): void => {
      subscription.unsubscribe();
      presentationSubscription.unsubscribe();
    };
  }, [ownership, relay, telemetryLifecycle]);

  useEffect(() => {
    if (deferredUnmount.timer !== null) {
      clearTimeout(deferredUnmount.timer);
      deferredUnmount.timer = null;
    }
    let activeConfirmed = false;
    let intentionalEnd = false;
    const hasOwnMembership = (): boolean =>
      rtcSession.memberships.some(
        (membership) =>
          membership.sender === client.getUserId() &&
          membership.deviceId === client.getDeviceId() &&
          !membership.isExpired(),
      );
    const isRecovering = (): boolean => {
      const phase = ownership.coordinator.state$.value.phase;
      return (
        phase === "recovering" ||
        phase === "waiting_for_network" ||
        phase === "verifying"
      );
    };
    const confirmActive = (): void => {
      if (
        livekitRoom.state === ConnectionState.Connected &&
        hasOwnMembership()
      ) {
        activeConfirmed = true;
      }
    };
    const escalate = (signal: CallRecoverySignal): boolean => {
      if (
        intentionalEnd ||
        (!activeConfirmed && signal.type !== "initial_connection_failed")
      ) {
        return false;
      }
      void ownership.coordinator.escalate(signal);
      return true;
    };
    const detachRelay = relay.attach(escalate);
    const onMembershipError = (error: unknown): void => {
      escalate({ type: "membership_manager_failed", error });
    };
    const onMembershipsChanged = (): void => {
      if (hasOwnMembership()) {
        confirmActive();
      }
    };
    const onConnected = (): void => confirmActive();
    const onDisconnected = (reason?: DisconnectReason): void => {
      if (!isRecovering()) escalate({ type: "livekit_disconnected", reason });
    };
    const onLocalTrackUnpublished = (
      publication: LocalTrackPublication,
    ): void => {
      if (
        !isRecovering() &&
        livekitRoom.state === ConnectionState.Connected &&
        ((publication.source === Track.Source.Microphone &&
          latestMuteStates.current?.audio.enabled) ||
          (publication.source === Track.Source.Camera &&
            latestMuteStates.current?.video.enabled))
      ) {
        escalate({
          type: "required_track_failed",
          error: new Error(
            `Обязательная публикация ${publication.source} потеряна`,
          ),
        });
      }
    };
    const onOnline = (): void => {
      void ownership.coordinator.escalate({ type: "network_online" });
    };

    rtcSession.on(
      MatrixRTCSessionEvent.MembershipManagerError,
      onMembershipError,
    );
    rtcSession.on(
      MatrixRTCSessionEvent.MembershipsChanged,
      onMembershipsChanged,
    );
    livekitRoom.on(RoomEvent.Connected, onConnected);
    livekitRoom.on(RoomEvent.Disconnected, onDisconnected);
    /*
       Сторож потока: LiveKit признаёт путь мёртвым через пятнадцать секунд, человек
       слышит тишину всё это время. Полторы секунды без пакетов задумывались как более
       ранний признак обрыва.

       ПЕРЕЗАХОД ПО ЭТОМУ ПРИЗНАКУ ВЫКЛЮЧЕН (28.09.2026). На общей встрече из тринадцати
       человек он сработал 151 раз за день: у троих на сборке 1.12.134 звонок уходил в
       перезаход каждые 15–20 секунд, встречу пришлось отменить. Причина в самом признаке:
       в большой комнате поток чужого звука останавливается штатно — молчащий собеседник
       с подавлением тишины шлёт пакеты редко, а сервер приостанавливает пересылку не
       говорящих. Тишина в счётчиках не равна обрыву пути.

       Сам счёт оставляем: строка в журнале нужна, чтобы перемерить порог и дописать
       условие «пакеты хоть раз приходили». Включить обратно — localStorage
       klava-stall-recovery = on.
    */
    const stallRecoveryEnabled =
      (() => {
        try {
          return localStorage.getItem("klava-stall-recovery") === "on";
        } catch {
          return false;
        }
      })();
    const stopStallWatch = watchMediaStalls(livekitRoom, {
      onStall: (silentMs) => {
        if (!stallRecoveryEnabled) return;
        if (!isRecovering()) escalate({ type: "media_stalled", silentMs });
      },
    });
    livekitRoom.localParticipant.on(
      "localTrackUnpublished",
      onLocalTrackUnpublished,
    );
    window.addEventListener("online", onOnline);
    confirmActive();

    return (): void => {
      intentionalEnd = true;
      detachRelay();
      rtcSession.off(
        MatrixRTCSessionEvent.MembershipManagerError,
        onMembershipError,
      );
      rtcSession.off(
        MatrixRTCSessionEvent.MembershipsChanged,
        onMembershipsChanged,
      );
      stopStallWatch();
      livekitRoom.off(RoomEvent.Connected, onConnected);
      livekitRoom.off(RoomEvent.Disconnected, onDisconnected);
      livekitRoom.localParticipant.off(
        "localTrackUnpublished",
        onLocalTrackUnpublished,
      );
      window.removeEventListener("online", onOnline);
      // React StrictMode синхронно проигрывает cleanup/setup одного mount.
      // Отложенный teardown отменяется повторным setup, но настоящий unmount
      // всё равно поглощает зависшие и поздние technical promises.
      deferredUnmount.timer = setTimeout(() => {
        deferredUnmount.timer = null;
        try {
          ownership.coordinator.cancel("unmount");
        } finally {
          // Observer обязан увидеть синхронный final emission cancel до detach.
          telemetryLifecycle.subscription?.unsubscribe();
          telemetryLifecycle.subscription = null;
        }
      }, 0);
    };
  }, [
    client,
    deferredUnmount,
    latestMuteStates,
    livekitRoom,
    ownership,
    relay,
    rtcSession,
    telemetryLifecycle,
  ]);

  const cancel = useCallback(
    (reason: "user_leave" | "unmount" | "host_hangup"): void => {
      ownership.coordinator.cancel(reason);
    },
    [ownership],
  );

  return {
    coordinator: ownership.coordinator,
    roomMembersHydrator: ownership.roomMembersHydrator,
    rtcMembersHydrator: ownership.rtcMembersHydrator,
    presentation: ownership.presentation,
    presentationState$: ownership.presentation.state$,
    recoveryActive,
    cancel,
  };
}
